import { useState, useEffect, useCallback } from 'react';
import { getDistributorFiles, uploadDistributorFile, deleteDistributorFile, downloadFileWithName } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { File as FileIcon, Trash, DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DistributorDocuments() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileLabel, setFileLabel] = useState('');

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDistributorFiles();
      setFiles(res.data);
    } catch (err) {
      toast.error('Files load nahi hue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', fileLabel || file.name);
      await uploadDistributorFile(fd);
      toast.success('File uploaded!');
      setFileLabel('');
      e.target.value = '';
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail hua');
    } finally {
      setFileUploading(false);
    }
  };

  const handleDeleteFile = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteDistributorFile(id);
      toast.success('File deleted');
      fetchFiles();
    } catch (err) {
      toast.error('Delete fail hua');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (ct) => {
    if (ct?.includes('pdf')) return 'text-red-500';
    if (ct?.includes('spreadsheet') || ct?.includes('excel')) return 'text-green-500';
    if (ct?.includes('image')) return 'text-blue-500';
    return 'text-gray-500';
  };

  if (loading) {
    return <div className="p-6 flex justify-center items-center min-h-[50vh]"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="distributor-documents">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Documents</h1>
        <p className="text-sm text-gray-500 mt-0.5">NEFT receipts, bank statements ya koi bhi document upload karein</p>
      </div>

      {/* Upload Area */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="file-upload-area">
        <h2 className="text-base font-semibold mb-3">Upload New Document</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs text-gray-500 mb-1 block">Label (optional)</Label>
            <Input
              value={fileLabel}
              onChange={e => setFileLabel(e.target.value)}
              placeholder="e.g. NEFT Receipt March, Bank Statement"
              className="text-sm"
              data-testid="file-label-input"
            />
          </div>
          <div className="relative">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.xlsx,.xls,.csv,.txt"
              onChange={handleFileUpload}
              disabled={fileUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              data-testid="file-upload-input"
            />
            <Button variant="outline" className="gap-2 text-sm w-full sm:w-auto" disabled={fileUploading}>
              <UploadSimple size={16} />
              {fileUploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Excel, CSV, Text, PDF, Images (max 10MB)</p>
      </div>

      {/* Files List */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="distributor-files-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">All Documents</h2>
          <span className="text-xs text-gray-400">{files.length} file(s)</span>
        </div>
        {files.length > 0 ? (
          <div className="space-y-2">
            {files.map(f => (
              <div key={f.id} className="flex flex-col sm:flex-row sm:items-center justify-between border border-gray-100 rounded-lg px-4 py-3 hover:bg-gray-50 transition-colors gap-2" data-testid={`file-row-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileIcon size={20} weight="fill" className={getFileIcon(f.content_type)} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{f.label}</p>
                    <p className="text-xs text-gray-400">{f.original_name} · {formatFileSize(f.size)} · {formatDate(f.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" onClick={() => downloadFileWithName(`${window.location.origin}/api/distributor/files/${f.id}/download`, f.url, f.original_name)} data-testid={`download-file-${f.id}`}>
                    <DownloadSimple size={18} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteFile(f.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-file-${f.id}`}>
                    <Trash size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No documents uploaded yet</p>
        )}
      </div>
    </div>
  );
}
